import React from 'react';
import { Box, Container, Typography, Link, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import TelegramIcon from '@mui/icons-material/Telegram';
import LanguageIcon from '@mui/icons-material/Language';
import { motion } from 'framer-motion';

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(63, 160, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  borderTop: '1px solid rgba(63, 160, 255, 0.15)',
  boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  zIndex: theme.zIndex.appBar - 1,
  marginTop: 'auto',
  padding: theme.spacing(6, 0),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
      linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
    `,
    backgroundSize: '60px 60px',
    opacity: 0.8,
    zIndex: 2
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '150%',
    height: '150%',
    background: 'radial-gradient(circle at center, rgba(63, 160, 255, 0.06) 0%, transparent 70%)',
    opacity: 0.7,
    zIndex: 1
  }
}));

const ContactLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 3,
  '&:hover': {
    color: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    '& .MuiSvgIcon-root': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  },
  '& .MuiSvgIcon-root': {
    transition: 'all 0.3s ease',
  }
}));

const MotionBox = motion(Box);

const Footer = () => {
  const theme = useTheme();

  return (
    <FooterContainer id="footer">
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 4,
            position: 'relative',
            zIndex: 3
          }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                textAlign: { xs: 'center', md: 'left' },
                mb: 2
              }}
            >
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ContactLink href="mailto:borislav.malinov@nextbasket.com">
                <EmailIcon /> borislav.malinov@nextbasket.com
              </ContactLink>
              <ContactLink href="https://t.me/BoboAN" target="_blank" rel="noopener noreferrer">
                <TelegramIcon /> @BoboAN
              </ContactLink>
              <ContactLink href="https://www.nextbasket.com" target="_blank" rel="noopener noreferrer">
                <LanguageIcon /> www.nextbasket.com
              </ContactLink>
            </Box>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            sx={{ textAlign: { xs: 'center', md: 'right' } }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              © NEXT BASKET – Presentation Portal
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              
            </Typography>
            <Box
              component="img"
              src="/NB-logo.png"
              alt="Next Basket Logo"
              sx={{
                height: { xs: '30px', md: '40px' },
                width: 'auto',
                opacity: 0.7,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: 'contrast(1.1)',
                '&:hover': {
                  opacity: 1,
                  transform: 'scale(1.05) rotate(-2deg)',
                  filter: 'contrast(1.2) brightness(1.1)'
                },
              }}
            />
          </MotionBox>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
